import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CarVatType, IProductViewModel, ProductLeasingType } from '@ncg/data';
import { Subject } from 'rxjs';
import { ScrollService } from '../../../core/scroll.service';

import { menuBarAnimation } from '../../../utils/animations/filter.animation';

@Component({
    selector: 'ncg-product-price',
    template: `
        <div class="product-price columns is-mobile is-multiline">
            <div class="column is-full">
                <nav class="navbar product-price__navbar" role="navigation" aria-label="Price information">
                    <div class="navbar-start" #navRef>
                        <button
                            *ngIf="showCashPrice"
                            #btn1
                            class="button is-filter product-price__menu-btn"
                            [ngClass]="{ 'is-active-bar': activeTag === 1 }"
                            (click)="setTag(1, btn1)"
                        >
                            {{ 'product_details.cash_price_header' | translate }}
                        </button>
                        <button
                            *ngIf="showLeasingPrice"
                            #btn2
                            class="button is-filter product-price__menu-btn"
                            [ngClass]="{ 'is-active-bar': activeTag === 2 }"
                            (click)="setTag(2, btn2)"
                        >
                            <ng-container [ngSwitch]="product.leasingType">
                                <ng-container *ngSwitchCase="productLeasingType.noLeasing">
                                    {{ 'product_details.leasing_price_header' | translate }}
                                </ng-container>
                                <ng-container *ngSwitchCase="productLeasingType.businessFinancial">
                                    {{ 'product_details.leasing_price_header_business' | translate }}
                                </ng-container>
                                <ng-container *ngSwitchCase="productLeasingType.businessOperational">
                                    {{ 'product_details.leasing_price_header_business' | translate }}
                                </ng-container>
                                <ng-container *ngSwitchCase="productLeasingType.privateFinancial">
                                    {{ 'product_details.leasing_price_header_private' | translate }}
                                </ng-container>
                                <ng-container *ngSwitchCase="productLeasingType.privateOperational">
                                    {{ 'product_details.leasing_price_header_private' | translate }}
                                </ng-container>
                            </ng-container>
                        </button>
                        <div
                            [@menuBar]="{
                                value: state,
                                params: {
                                    scaleToPercentage: scaleToPercentage,
                                    scalePercentage: scalePercentage,
                                    fromX: fromX + 'px',
                                    toX: toX + 'px'
                                }
                            }"
                            #activeBarRef
                            class="active-bar"
                        ></div>
                    </div>
                </nav>
            </div>
            <div class="column product-price__content">
                <div class="product-price__cash" *ngIf="activeTag === 1">
                    <ng-container *ngIf="product.cashPrice && product.priceType !== 'CallForPrice'; else callForPrice">
                        <div class="product-price__amount product-price__amount--main">
                            {{ product.cashPrice }}
                        </div>
                        <p class="produt-price__psa">{{ 'product_details.cash_price_psa' | translate }}</p>
                    </ng-container>
                    <ng-template #callForPrice>
                        <ncg-rich-text [html]="callForPricePsa"></ncg-rich-text>
                    </ng-template>

                    <div class="product-price__buttons">
                        <ncg-send-offer-button [buttonClass]="'button is-primary is-fullwidth'"></ncg-send-offer-button>
                        <ncg-test-drive-button [buttonClass]="'button is-primary is-fullwidth'"></ncg-test-drive-button>
                        <ncg-get-price-button [buttonClass]="'button is-primary is-fullwidth'"></ncg-get-price-button>
                        <ncg-favorites-page-link [iconClass]="'is-hidden'" [linkClass]="'is-fullwidth'"></ncg-favorites-page-link>
                        <ncg-seez-buy-button
                            [buttonClass]="'button is-success is-fullwidth'"
                            [productId]="product?.properties?.id"
                        ></ncg-seez-buy-button>
                    </div>

                    <div class="product-price__psa" *ngIf="product.priceType !== 'CallForPrice'" [ngSwitch]="product.properties?.vatType">
                        <ncg-rich-text *ngSwitchCase="vatType.WithoutVat" [html]="cashPriceNoVatPSA"></ncg-rich-text>
                        <ncg-rich-text *ngSwitchDefault [html]="cashPricePsa"></ncg-rich-text>
                    </div>
                </div>
                <div class="product-price__leasing" *ngIf="activeTag === 2">
                    <ng-container *ngIf="product.leasingPrice && product.priceType !== 'CallForLeasing'; else callForLeasing">
                        <div class="product-price__amount product-price__amount--main">
                            {{ 'product_details.leasing_price' | translate: { amount: product.leasingPrice } }}
                        </div>
                        <div class="product-price__psa">
                            <table
                                *ngIf="
                                    product.leasingType === productLeasingType.businessFinancial ||
                                    product.leasingType === productLeasingType.businessOperational ||
                                    product.leasingType === productLeasingType.privateFinancial ||
                                    product.leasingType === productLeasingType.privateOperational
                                "
                                [attr.aria-label]="'product_details.leasing_table_description' | translate"
                            >
                                <tr *ngIf="product.leasingDuration">
                                    <th>{{ 'product_details.leasing_duration_short' | translate }}</th>
                                    <td>{{ product.leasingDuration }} {{ 'product_details.leasing_month_value_short' | translate }}</td>
                                </tr>
                                <tr *ngIf="product.leasingYearlyMileage">
                                    <th>{{ 'product_details.leasing_yearly_mileage_short' | translate }}</th>
                                    <td>{{ product.leasingYearlyMileage }}</td>
                                </tr>
                                <tr *ngIf="product.leasingDownPayment">
                                    <th>{{ 'product_details.leasing_downpayment_short' | translate }}</th>
                                    <td>{{ product.leasingDownPayment }}</td>
                                </tr>
                                <tr *ngIf="product.leasingPrice">
                                    <th>{{ 'product_details.leasing_price_short' | translate }}</th>
                                    <td>{{ 'product_details.leasing_price_value_monthly' | translate: { amount: product.leasingPrice } }}</td>
                                </tr>
                                <ng-container
                                    *ngIf="
                                        product.leasingType === productLeasingType.businessFinancial ||
                                        product.leasingType === productLeasingType.privateFinancial
                                    "
                                >
                                    <tr *ngIf="product.leasingResidualValue" class="product-price__residual-value">
                                        <th>{{ 'product_details.leasing_residualvalue_short' | translate }}</th>
                                        <td>{{ product.leasingResidualValue }}</td>
                                    </tr>
                                </ng-container>
                                <tr *ngIf="product.leasingTotalPayment">
                                    <th>{{ 'product_details.leasing_total_payment_short' | translate }}</th>
                                    <td>{{ product.leasingTotalPayment }}</td>
                                </tr>
                            </table>
                            <br />
                            <p *ngIf="!hideLeasingTexts" [ngSwitch]="product.leasingType">
                                <ng-container *ngSwitchCase="productLeasingType.businessFinancial">
                                    {{ 'product_details.leasing_text_business_financial' | translate: product }}
                                </ng-container>
                                <ng-container *ngSwitchCase="productLeasingType.businessOperational">
                                    {{ 'product_details.leasing_text_business_operational' | translate: product }}
                                </ng-container>
                                <ng-container *ngSwitchCase="productLeasingType.privateFinancial">
                                    {{ 'product_details.leasing_text_private_financial' | translate: product }}
                                </ng-container>
                                <ng-container *ngSwitchCase="productLeasingType.privateOperational">
                                    {{ 'product_details.leasing_text_private_operational' | translate: product }}
                                </ng-container>
                            </p>
                        </div>
                    </ng-container>
                    <ng-template #callForLeasing>
                        <ncg-rich-text [html]="callForLeasingPsa"></ncg-rich-text>
                    </ng-template>

                    <div class="columns is-multiline is-variable is-2 is-mobile">
                        <div class="column is-6-fullhd">
                            <ncg-send-offer-button [buttonClass]="'button is-primary is-full'"></ncg-send-offer-button>
                        </div>
                        <div class="column is-6-fullhd">
                            <ncg-test-drive-button [buttonClass]="'button is-primary is-full'"></ncg-test-drive-button>
                        </div>
                    </div>

                    <div class="product-price__psa pt-0" [ngSwitch]="product.leasingType" *ngIf="product.priceType !== 'CallForLeasing'">
                        <ng-container *ngSwitchCase="productLeasingType.businessFinancial">
                            <ncg-rich-text *ngIf="leasingPriceNoVatPsa" [html]="leasingPriceNoVatPsa"></ncg-rich-text>
                        </ng-container>
                        <ng-container *ngSwitchCase="productLeasingType.businessOperational">
                            <ncg-rich-text *ngIf="leasingPriceNoVatPsa" [html]="leasingPriceNoVatPsa"></ncg-rich-text>
                        </ng-container>
                        <ng-container *ngSwitchCase="productLeasingType.privateFinancial">
                            <ncg-rich-text *ngIf="leasingPricePsa" [html]="leasingPricePsa"></ncg-rich-text>
                        </ng-container>
                        <ng-container *ngSwitchCase="productLeasingType.privateOperational">
                            <ncg-rich-text *ngIf="leasingPricePsa" [html]="leasingPricePsa"></ncg-rich-text>
                        </ng-container>
                    </div>
                </div>
            </div>
        </div>
    `,
    animations: [menuBarAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductPriceComponent implements OnInit, OnDestroy {
    @Input() product: IProductViewModel;
    @Input() cashPricePsa?: string;
    @Input() cashPriceNoVatPSA?: string;
    @Input() leasingPricePsa?: string;
    @Input() leasingPriceNoVatPsa?: string;
    @Input() callForPricePsa?: string;
    @Input() callForLeasingPsa?: string;
    @Input() hideLeasingTexts?: boolean;

    productLeasingType = ProductLeasingType;
    vatType = CarVatType;

    navRef?: ElementRef<HTMLDivElement>;
    activeBarRef?: ElementRef<HTMLDivElement>;

    @ViewChild('navRef', { static: false }) set nav(ele: ElementRef<HTMLDivElement>) {
        this.navRef = ele;
    }

    @ViewChild('activeBarRef', { static: false }) set activeBar(ele: ElementRef<HTMLDivElement>) {
        this.activeBarRef = ele;
        if (!this.isActiveBarSet && ele.nativeElement.offsetHeight) {
            this.initializeFilterButton();
            this.isActiveBarSet = true;
        }
    }

    isActiveBarSet = false;
    activeTag: number;
    state: 'initial' | 'move' = 'initial';
    scaleToPercentage = 0; // Size of the active button in percentage
    scalePercentage = 0; // The scaling size between active and previous button
    prevButton?: HTMLButtonElement;
    toButton: HTMLButtonElement;
    fromX = 0;
    toX = 0;
    direction: 'leftToRight' | 'rightToLeft' = 'leftToRight';

    showCashPrice = true;
    showLeasingPrice = true;
    private readonly unsubscribe = new Subject<void>();

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly scrollService: ScrollService
    ) {}

    ngOnInit() {
        if (this.product) {
            this.showCashPrice = !!this.product.cashPrice || this.product.priceType === 'CallForPrice';
            this.showLeasingPrice = !!this.product.leasingPrice || this.product.priceType === 'CallForLeasing';

            if (this.showCashPrice) {
                this.activeTag = 1;
            } else if (this.showLeasingPrice) {
                this.activeTag = 2;
            } else {
                this.activeTag = 1;
                this.showCashPrice = true;
                this.product.priceType = 'CallForPrice';
            }
        }
    }

    initializeFilterButton() {
        if (this.navRef) {
            const firstBtn = this.navRef.nativeElement.querySelector('button');
            if (firstBtn) {
                this.setTag(this.activeTag, firstBtn);
            }
        }
    }

    setTag(tag: number, btnElement?: HTMLButtonElement): void {
        this.activeTag = tag;

        if (this.navRef && btnElement) {
            this.prevButton = this.toButton;
            this.toButton = btnElement;
            this.scrollToButtonElement();
            this.animateActiveBar();
        }
    }

    scrollToButtonElement() {
        if (!this.navRef) {
            return;
        }

        let scrollNumber;
        const itemOffsetLeft = this.toButton.offsetLeft;
        const itemWidth = this.toButton.clientWidth;
        const nav: HTMLElement = this.navRef.nativeElement;
        const navWidth = nav.offsetWidth;
        const navOffsetLeft = nav.scrollLeft;
        const btnPadding = 35; // used for to show some of the next menu item

        if (itemWidth + itemOffsetLeft > navWidth + navOffsetLeft) {
            scrollNumber = itemWidth + itemOffsetLeft - navWidth;
            scrollNumber = scrollNumber + btnPadding;
        } else if (navOffsetLeft > itemOffsetLeft) {
            scrollNumber = itemOffsetLeft;
            scrollNumber = scrollNumber - btnPadding;
        } else {
            return;
        }

        this.scrollService.scrollToPosition({ left: scrollNumber }, nav);
    }

    animateActiveBar(isResize?: boolean) {
        if (!this.navRef?.nativeElement || !this.activeBarRef?.nativeElement) {
            return;
        }

        const nav: HTMLElement = this.navRef.nativeElement;
        const btnStyle = window.getComputedStyle(this.toButton);
        const btnPl = btnStyle.paddingLeft ? Number(btnStyle.paddingLeft.replace('px', '')) : 0;
        const btnPr = btnStyle.paddingRight ? Number(btnStyle.paddingRight.replace('px', '')) : 0;
        const padding = btnPl + btnPr;
        const width = this.toButton.clientWidth - padding;
        this.scaleToPercentage = width / nav.offsetWidth;
        this.toX = this.toButton.offsetLeft + btnPl;

        // Only set new width an X coordinates when resizing window
        if (isResize) {
            return;
        }

        if (this.prevButton) {
            const prevBtnStyle = window.getComputedStyle(this.prevButton);
            const prevBtnPl = prevBtnStyle.paddingLeft ? Number(prevBtnStyle.paddingLeft.replace('px', '')) : 0;
            const prevBtnPr = prevBtnStyle.paddingRight ? Number(prevBtnStyle.paddingRight.replace('px', '')) : 0;
            const prevPadding = prevBtnPl + prevBtnPr;
            const prevWidth = this.prevButton.clientWidth - prevPadding;

            this.direction = this.prevButton.offsetLeft < this.toButton.offsetLeft ? 'leftToRight' : 'rightToLeft';

            if (this.direction === 'leftToRight') {
                this.fromX = this.prevButton.offsetLeft + prevBtnPl;
                this.scalePercentage = (this.toButton.offsetLeft + btnPl + width - (this.prevButton.offsetLeft + prevBtnPl)) / nav.offsetWidth;
            } else {
                this.fromX = this.toButton.offsetLeft + btnPl;
                this.scalePercentage = (this.prevButton.offsetLeft + prevBtnPl + prevWidth - (this.toButton.offsetLeft + btnPl)) / nav.offsetWidth;
            }
        } else {
            this.fromX = 0;
            this.scalePercentage = this.toX / nav.offsetWidth + this.scaleToPercentage;
        }

        this.state = this.state === 'initial' ? 'move' : 'initial';
        this.cd.detectChanges();
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
